// src/pages/AdminUsers.js
import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, Table,
    Spinner, Alert, Form, InputGroup, Button, Badge
} from 'react-bootstrap';
import { Search, PencilSquare, XCircleFill, CheckCircleFill } from 'react-bootstrap-icons';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [processing, setProcessing] = useState(null);

    const { t } = useTranslation();

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/admin/users');
            setUsers(response.data.data || []);
        } catch (err) {
            setError(err.response?.data?.message || t('errors.loadingError'));
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const toggleAdminRole = async (userId, isCurrentlyAdmin) => {
        setProcessing(userId);
        try {
            // Предполагаем, что на бэкенде есть такой эндпоинт
            await api.post('/api/admin/users/toggle-admin', {
                userId,
                isAdmin: !isCurrentlyAdmin
            });

            // Обновляем список пользователей после изменения
            setUsers(users.map(user =>
                user._id === userId
                    ? {...user, isAdmin: !isCurrentlyAdmin}
                    : user
            ));
        } catch (err) {
            setError(err.response?.data?.message || t('errors.updateError'));
            console.error('Error toggling admin role:', err);
        } finally {
            setProcessing(null);
        }
    };

    // Фильтрация по поисковому запросу
    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(search.toLowerCase())
    );

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    return (
        <Container className="my-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1>{t('admin.users')}</h1>
            </div>

            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            <Card className="glass-card">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{t('admin.usersList')}</h5>
                    <Form.Group style={{ width: '300px' }}>
                        <InputGroup>
                            <InputGroup.Text>
                                <Search />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder={t('admin.userSearch')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </InputGroup>
                    </Form.Group>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">{t('common.loading')}</p>
                        </div>
                    ) : filteredUsers.length > 0 ? (
                        <div className="table-responsive">
                            <Table hover className="web3-table">
                                <thead>
                                <tr>
                                    <th>{t('common.user')}</th>
                                    <th>{t('admin.permissions')}</th>
                                    <th>{t('referral.registrationDate')}</th>
                                    <th>{t('referral.lastLogin')}</th>
                                    <th>{t('dashboard.flightSheets')}</th>
                                    <th>{t('dashboard.activeWorkers')}</th>
                                    <th>{t('dashboard.hashrate')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredUsers.map(user => (
                                    <tr key={user._id}>
                                        <td>
                                            <strong>{user.username}</strong>
                                        </td>
                                        <td>
                                            {user.isAdmin ? (
                                                <Badge bg="warning" pill>{t('common.admin')}</Badge>
                                            ) : (
                                                <Badge bg="secondary" pill>{t('common.user')}</Badge>
                                            )}
                                            {user.isSeller && (
                                                <Badge bg="info" className="ms-1" pill>{t('seller.seller')}</Badge>
                                            )}
                                        </td>
                                        <td>{formatDate(user.createdAt)}</td>
                                        <td>{formatDate(user.lastLogin)}</td>
                                        <td>{user.flightSheetsCount || 0}</td>
                                        <td>
                                            <Badge bg={user.activeWorkers > 0 ? 'success' : 'secondary'}>
                                                {user.activeWorkers || 0}
                                            </Badge>
                                        </td>
                                        <td>{user.hashrateFormatted || '0 H/s'}</td>
                                        <td>
                                            <Button
                                                variant={user.isAdmin ? "outline-warning" : "outline-primary"}
                                                size="sm"
                                                onClick={() => toggleAdminRole(user._id, user.isAdmin)}
                                                disabled={processing === user._id}
                                            >
                                                {processing === user._id ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : user.isAdmin ? (
                                                    <XCircleFill className="me-1" />
                                                ) : (
                                                    <CheckCircleFill className="me-1" />
                                                )}
                                                {user.isAdmin ? t('admin.removeAdmin') : t('admin.makeAdmin')}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            {search ? (
                                <p>{t('referral.noSearchResults')}</p>
                            ) : (
                                <p>{t('admin.noUsers')}</p>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default AdminUsers;